import React, { useState, useEffect, useRef } from 'react';
import { apiCall } from '../utils/api';
import { toast } from './ui/sonner';
import { useWebSocket } from '../contexts/WebSocketContext';
import {
  MessageSquare, Users, X, Send, ArrowLeft, Plus,
  User, Loader2, Check, ChevronRight
} from 'lucide-react';

export default function ShiftGroupChat({ shift, onClose }) {
  const [view, setView] = useState('workers'); // 'workers' | 'chats' | 'chat' | 'create'
  const [workers, setWorkers] = useState([]);
  const [groupChats, setGroupChats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const debounceRef = useRef(null);
  const { lastMessage } = useWebSocket();

  const currentUserId = JSON.parse(localStorage.getItem('user'))?._id;

  useEffect(() => {
    loadWorkers();
    loadGroupChats();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // WS push → refetch active chat on new_message events. Falls back to the
  // 30s safety-net poll set up in openChat().
  useEffect(() => {
    if (!activeChat) return;
    if (!lastMessage || lastMessage.type !== 'new_message') return;
    const chatId = lastMessage.chat_id || lastMessage.data?.chat_id;
    if (chatId && String(chatId) !== String(activeChat.chat_id)) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => loadMessages(activeChat.chat_id), 300);
  }, [lastMessage, activeChat]);

  const loadWorkers = async () => {
    setLoading(true);
    try {
      const res = await apiCall(`/api/worker/shift/${shift._id}/workers`);
      setWorkers(res.workers || []);
    } catch { setWorkers([]); }
    finally { setLoading(false); }
  };

  const loadGroupChats = async () => {
    try {
      const res = await apiCall(`/api/worker/shift/${shift._id}/group-chats`);
      setGroupChats(res.chats || []);
    } catch {}
  };

  const openChat = async (chat) => {
    setActiveChat(chat);
    setView('chat');
    await loadMessages(chat.chat_id);
    // Safety-net poll — 30s (WS-first, poll is fallback)
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => loadMessages(chat.chat_id), 30000);
  };

  const loadMessages = async (chatId) => {
    try {
      const res = await apiCall(`/api/worker/group-chat/${chatId}/messages`);
      setMessages(res.messages || []);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch {}
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeChat) return;
    setSending(true);
    try {
      await apiCall(`/api/worker/group-chat/${activeChat.chat_id}/messages`, {
        method: 'POST', body: JSON.stringify({ message: newMessage })
      });
      setNewMessage('');
      await loadMessages(activeChat.chat_id);
    } catch { toast.error('Failed to send'); }
    finally { setSending(false); }
  };

  const createGroupChat = async () => {
    if (selectedMembers.length === 0) { toast.error('Select at least one person'); return; }
    try {
      const res = await apiCall(`/api/worker/shift/${shift._id}/group-chats`, {
        method: 'POST',
        body: JSON.stringify({ member_ids: selectedMembers, name: groupName })
      });
      toast.success('Group chat created!');
      setSelectedMembers([]);
      setGroupName('');
      loadGroupChats();
      openChat(res.chat);
    } catch (e) { toast.error(e.message || 'Failed to create chat'); }
  };

  const toggleMember = (id) => {
    setSelectedMembers(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);
  };

  const otherWorkers = workers.filter(w => w.id !== currentUserId);

  // Direct message via existing messaging
  const handleDirectMessage = (worker) => {
    toast.info(`Opening DM with ${worker.name}...`);
    // This would integrate with the existing MessagingInbox
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="shift-group-chat-modal">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-700 to-slate-900 p-4 text-white flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {(view === 'chat' || view === 'create') && (
                <button onClick={() => { setView('workers'); setActiveChat(null); if (pollRef.current) clearInterval(pollRef.current); }} className="p-1 hover:bg-white/20 rounded-lg">
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              <div className="p-2 bg-white/20 rounded-lg">
                {view === 'chat' ? <MessageSquare className="w-5 h-5" /> : <Users className="w-5 h-5" />}
              </div>
              <div>
                <h3 className="font-bold text-sm">
                  {view === 'chat' ? (activeChat?.name || 'Group Chat') : view === 'create' ? 'New Group Chat' : 'Shift Group Chat'}
                </h3>
                <p className="text-xs text-white/70">
                  {view === 'chat' ? `${activeChat?.members?.length || 0} members` : shift.title}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg" data-testid="close-group-chat">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Workers List View */}
        {view === 'workers' && (
          <div className="flex-1 overflow-y-auto">
            {/* Existing Group Chats */}
            {groupChats.length > 0 && (
              <div className="border-b border-gray-100">
                <div className="px-4 py-2 bg-gray-50">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Group Chats</p>
                </div>
                {groupChats.map(chat => (
                  <button
                    key={chat.chat_id}
                    onClick={() => openChat(chat)}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition border-b border-gray-50"
                    data-testid={`group-chat-${chat.chat_id}`}
                  >
                    <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="text-sm font-semibold text-gray-900">{chat.name}</p>
                      <p className="text-xs text-gray-500">{chat.members?.length || 0} members</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </button>
                ))}
              </div>
            )}

            {/* Workers on Shift */}
            <div className="px-4 py-2 bg-gray-50 flex items-center justify-between">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Workers on This Shift</p>
              <span className="text-xs text-gray-400">{otherWorkers.length} {otherWorkers.length === 1 ? 'person' : 'people'}</span>
            </div>

            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
            ) : otherWorkers.length === 0 ? (
              <div className="py-8 text-center text-gray-400 text-sm">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p>No other workers on this shift yet</p>
              </div>
            ) : (
              <div>
                {otherWorkers.map(worker => (
                  <div key={worker.id} className="px-4 py-3 flex items-center gap-3 border-b border-gray-50" data-testid={`shift-worker-${worker.id}`}>
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 font-bold text-sm">
                      {worker.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{worker.name}</p>
                      <p className="text-xs text-gray-500">{worker.email}</p>
                    </div>
                    <button
                      onClick={() => handleDirectMessage(worker)}
                      className="px-2.5 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition"
                      data-testid={`dm-worker-${worker.id}`}
                    >
                      Send Message
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Create Group Chat Button */}
            {otherWorkers.length > 0 && (
              <div className="p-4 border-t border-gray-100">
                <button
                  onClick={() => setView('create')}
                  className="w-full py-2.5 bg-indigo-600 text-white rounded-xl font-medium text-sm hover:bg-indigo-700 transition flex items-center justify-center gap-2"
                  data-testid="create-group-chat-btn"
                >
                  <Plus className="w-4 h-4" />
                  Create Group Chat
                </button>
              </div>
            )}
          </div>
        )}

        {/* Create Group Chat View */}
        {view === 'create' && (
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 space-y-3">
              <input
                type="text"
                value={groupName}
                onChange={e => setGroupName(e.target.value)}
                placeholder="Group name (optional)"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm"
                data-testid="group-name-input"
              />
              <p className="text-xs font-semibold text-gray-500">Select members:</p>
            </div>
            <div>
              {otherWorkers.map(worker => (
                <button
                  key={worker.id}
                  onClick={() => toggleMember(worker.id)}
                  className={`w-full px-4 py-3 flex items-center gap-3 border-b border-gray-50 transition ${selectedMembers.includes(worker.id) ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}
                  data-testid={`select-member-${worker.id}`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition ${selectedMembers.includes(worker.id) ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'}`}>
                    {selectedMembers.includes(worker.id) && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 font-bold text-xs">
                    {worker.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-gray-900">{worker.name}</p>
                    <p className="text-xs text-gray-500">{worker.email}</p>
                  </div>
                </button>
              ))}
            </div>
            <div className="p-4 border-t border-gray-100">
              <button
                onClick={createGroupChat}
                disabled={selectedMembers.length === 0}
                className="w-full py-2.5 bg-indigo-600 text-white rounded-xl font-medium text-sm hover:bg-indigo-700 transition disabled:opacity-40 flex items-center justify-center gap-2"
                data-testid="confirm-create-group-btn"
              >
                <Users className="w-4 h-4" />
                Create Chat ({selectedMembers.length} selected)
              </button>
            </div>
          </div>
        )}

        {/* Chat View */}
        {view === 'chat' && (
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
              {messages.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p>No messages yet. Say hello!</p>
                </div>
              ) : (
                messages.map((msg, i) => {
                  const isMe = msg.sender_id === currentUserId;
                  return (
                    <div key={i} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] ${isMe ? 'order-2' : ''}`}>
                        {!isMe && <p className="text-[10px] text-gray-500 mb-0.5 ml-1">{msg.sender_name}</p>}
                        <div className={`px-3 py-2 rounded-2xl text-sm ${isMe ? 'bg-indigo-600 text-white rounded-br-md' : 'bg-white text-gray-800 border border-gray-200 rounded-bl-md'}`} data-testid={`chat-message-${i}`}>
                          {msg.message}
                        </div>
                        <p className="text-[9px] text-gray-400 mt-0.5 ml-1">
                          {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="p-3 border-t border-gray-200 bg-white flex gap-2 flex-shrink-0">
              <input
                type="text"
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                placeholder="Type a message..."
                className="flex-1 px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                data-testid="group-chat-message-input"
              />
              <button
                onClick={sendMessage}
                disabled={sending || !newMessage.trim()}
                className="px-4 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition disabled:opacity-40"
                data-testid="group-chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
